import React, { useState } from 'react';
import { Button, Alert, Modal } from 'react-bootstrap';
import NoteModal from './NoteModal';
import CompactPrescriptionView from './CompactPrescriptionView';
import CompactReferralView from './CompactReferralView';
import PatientNotesHeaderActions from './patient/PatientNotes/PatientNotesHeaderActions';
import WhatsappMessageEntry from './patient/PatientNotes/WhatsappMessageEntry';
import OperationEntry from './patient/PatientNotes/OperationEntry';
import TimelineConnector from './patient/PatientNotes/TimelineConnector';
import usePatientNotesTimeline from './patient/PatientNotes/usePatientNotesTimeline';
import './PatientNotes.css';

const PatientNotes = ({ 
    notes, 
    notesLoading, 
    notesError, 
    whatsappMessages = [],
    whatsappMessagesLoading = false,
    whatsappMessagesError = '',
    onAddNote, 
    onAddMessage,
    onAddOperation,
    onEditMessage,
    onDeleteMessage,
    isMessageDeleting,
    operations = [],
    operationsLoading = false,
    operationsError = '',
    onEditOperation,
    onDeleteOperation,
    isOperationDeleting,
    onEditNote, 
    onDeleteNote,
    isNoteDeleting,
    showDeleteConfirm,
    handleModalClose,
    handleDeleteNote,
    deletingNoteId,
    isDeleting,
    deleteError,
    showNoteModal,
    setShowNoteModal,
    onSubmit,
    noteForm,
    onInputChange,
    submitting,
    submitError,
    editingNote,
    patientName = '',
    patientData = null,
}) => {
    const [showImageModal, setShowImageModal] = useState(false);
    const [currentImage, setCurrentImage] = useState('');
    const [currentImageName, setCurrentImageName] = useState('');
    // Track expanded state for referrals (keyed by referral id or composite key)
    const [expandedReferrals, setExpandedReferrals] = useState({});
    
    // Helper to render description: detect JSON and show a readable key/value layout
    const renderDescriptionContent = (description) => {
        if (!description || typeof description !== 'string') {
            return null;
        }

        const trimmed = description.trim();

        // Try to parse JSON only if it looks like JSON
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);

                if (parsed && typeof parsed === 'object') {
                    // Object: may have clinical_notes plus other fields
                    if (!Array.isArray(parsed)) {
                        const clinicalNotes = typeof parsed.clinical_notes === 'string' ? parsed.clinical_notes.trim() : '';

                        // Build list of remaining fields (exclude clinical_notes)
                        const entries = Object.entries(parsed).filter(([key, value]) => {
                            if (key === 'clinical_notes') return false;
                            return value !== null && value !== undefined && String(value).trim() !== '';
                        });

                        if (!clinicalNotes && entries.length === 0) {
                            return null;
                        }

                        return (
                            <div className="note-json-description mb-1" style={{ lineHeight: '1.6' }}>
                                {clinicalNotes && (
                                    <div className="mb-2">
                                        <div style={{ fontWeight: 600, marginBottom: '2px' }}>
                                            Clinical notes:
                                        </div>
                                        <div
                                            className="note-description"
                                            style={{ lineHeight: '1.5' }}
                                            dangerouslySetInnerHTML={{ __html: clinicalNotes }}
                                        />
                                    </div>
                                )}
                                {entries.map(([key, value]) => (
                                    <div key={key} className="d-flex mb-1">
                                        <div style={{ minWidth: '140px', fontWeight: 600 }}>
                                            {key.replace(/_/g, ' ')}:
                                        </div>
                                        <div style={{ whiteSpace: 'pre-wrap' }}>
                                            {typeof value === 'object'
                                                ? JSON.stringify(value, null, 2)
                                                : String(value)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    // Array: show each element on its own line
                    return (
                        <div className="note-json-description mb-1" style={{ lineHeight: '1.6' }}>
                            {parsed.map((item, index) => (
                                <div key={index} className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>
                                    {typeof item === 'object'
                                        ? JSON.stringify(item, null, 2)
                                        : String(item)}
                                </div>
                            ))}
                        </div>
                    );
                }
            } catch (e) {
                // Fall through to HTML rendering if JSON parsing fails
            }
        }

        // Default: treat as existing rich-text HTML
        return (
            <div
                className="mb-1 note-description"
                style={{ lineHeight: '1.5' }}
                dangerouslySetInnerHTML={{ __html: description || '' }}
            />
        );
    };

    const handleImageClick = (url, filename) => {
        setCurrentImage(url);
        setCurrentImageName(filename);
        setShowImageModal(true);
    };
    
    const handleCloseImageModal = () => {
        setShowImageModal(false);
        setCurrentImage('');
        setCurrentImageName('');
    };
    
    // Function to get file type icon
    const getFileIcon = (filename) => {
        const fileExt = filename?.split('.').pop().toLowerCase() || '';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt)) return 'fa-file-image';
        if (['pdf'].includes(fileExt)) return 'fa-file-pdf';
        if (['doc', 'docx'].includes(fileExt)) return 'fa-file-word';
        if (['xls', 'xlsx'].includes(fileExt)) return 'fa-file-excel';
        if (['zip', 'rar', '7z'].includes(fileExt)) return 'fa-file-archive';
        return 'fa-file';
    };

    const { timelineEntries, isTimelineLoading, timelineError } = usePatientNotesTimeline({
        notes,
        notesLoading,
        notesError,
        whatsappMessages,
        whatsappMessagesLoading,
        whatsappMessagesError,
        operations,
        operationsLoading,
        operationsError,
    });

    return (
        <div className="mt-5">
            <PatientNotesHeaderActions onAddMessage={onAddMessage} onAddOperation={onAddOperation} onAddNote={onAddNote} doctorId={patientData?.doctor_id} />

            {isTimelineLoading ? (
                <div className="text-center my-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : timelineError ? (
                <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {timelineError}
                </div>
            ) : timelineEntries.length === 0 ? (
                <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    No notes, messages, or operations found for this patient.
                </div>
            ) : (
                <div className="notes-timeline">
                    {(() => {
                        let runningLmp = null;
                        return timelineEntries.map((entry, index) => {
                            if (entry.kind === 'operation') {
                                const op = entry.data;
                                return (
                                    <React.Fragment key={`operation-${op.id}`}>
                                        <OperationEntry
                                            op={op}
                                            showConnector={index < timelineEntries.length - 1}
                                            onEditOperation={onEditOperation}
                                            onDeleteOperation={onDeleteOperation}
                                            isDeleting={isOperationDeleting ? isOperationDeleting(op.id) : false}
                                        />
                                    </React.Fragment>
                                );
                            }
                            if (entry.kind === 'message') {
                                const msg = entry.data;
                                return (
                                    <React.Fragment key={`whatsapp-${msg.id}`}>
                                        <WhatsappMessageEntry
                                            msg={msg}
                                            showConnector={index < timelineEntries.length - 1}
                                            onEditMessage={onEditMessage}
                                            onDeleteMessage={onDeleteMessage}
                                            isDeleting={isMessageDeleting ? isMessageDeleting(msg.id) : false}
                                        />
                                    </React.Fragment>
                                );
                            }

                            const note = entry.data;
                            // Update running LMP when this note has its own LMP value
                            if (note.lmp) {
                                runningLmp = note.lmp;
                            }

                            const renderGestationalAge = () => {
                                if (!runningLmp || !note.date) return null;
                                try {
                                    const [vy, vm, vd] = String(note.date).split('-');
                                    const [ly, lm, ld] = String(runningLmp).split('-');
                                    const visitDate = new Date(parseInt(vy), parseInt(vm) - 1, parseInt(vd));
                                    const lmpDate = new Date(parseInt(ly), parseInt(lm) - 1, parseInt(ld));

                                    if (isNaN(visitDate.getTime()) || isNaN(lmpDate.getTime())) return null;

                                    const diffMs = visitDate.getTime() - lmpDate.getTime();
                                    if (diffMs < 0) return null; // LMP after visit date: don't show GA

                                    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                                    const weeks = Math.floor(diffDays / 7);
                                    const days = diffDays % 7;

                                    // Optional safety cap: ignore absurd GAs
                                    if (weeks > 41) return null;

                                    return (
                                        <div className="small text-muted mt-1">
                                            GA: {weeks}w{days > 0 ? ` ${days}d` : ''}
                                        </div>
                                    );
                                } catch (e) {
                                    return null;
                                }
                            };

                            return (
                                <React.Fragment key={note.id}>
                                    <div className="list-group-item mb-0 note-card">
                                        <div className="note-content p-3">
                                            <div className="note-header-row mb-2 d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">
                                                    <span className="note-date">
                                                        <i className="fas fa-calendar-alt me-1" aria-hidden="true"></i>
                                                        {(() => {
                                                            const dateStr = note.date;
                                                            if (dateStr && dateStr.includes('-')) {
                                                                const [year, month, day] = dateStr.split('-');
                                                                const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                                                return date.toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                });
                                                            }
                                                            return dateStr;
                                                        })()}
                                                    </span>
                                                    {renderGestationalAge()}
                                                </h6>
                                                <div className="btn-group">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="d-flex align-items-center p-2"
                                                        onClick={() => onEditNote(note)}
                                                        title="Edit note"
                                                        aria-label="Edit note"
                                                        style={{ minWidth: '32px', minHeight: '32px' }}
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="d-flex align-items-center p-2"
                                                        onClick={() => onDeleteNote(note.id)}
                                                        disabled={isNoteDeleting && isNoteDeleting(note.id)}
                                                        title="Delete note"
                                                        aria-label="Delete note"
                                                        style={{ minWidth: '32px', minHeight: '32px' }}
                                                    >
                                                        {isNoteDeleting && isNoteDeleting(note.id) ? (
                                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                        ) : (
                                                            <i className="fas fa-trash"></i>
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>

                                            {note.type === 'appointment' ? (
                                                <>
                                                    {note.appointment_note && (
                                                        <div className="note-section">
                                                            <div className="section-title"><span className="icon">🗓️</span>Appointment</div>
                                                            <div
                                                                className="mb-2 appointment-summary"
                                                                style={{
                                                                    lineHeight: '1.5',
                                                                    fontStyle: 'italic',
                                                                    color: '#6c757d',
                                                                    backgroundColor: '#f8f9fa',
                                                                    borderRadius: '6px',
                                                                    padding: '6px 8px'
                                                                }}
                                                            >
                                                                <small className="text-muted d-block mb-1">{note.appointment_note}</small>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {note.description && (
                                                        <div className="note-section">
                                                            <div className="section-title"><span className="icon">📝</span>Clinical Notes</div>
                                                            {renderDescriptionContent(note.description)}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="note-section">
                                                    <div className="section-title"><span className="icon">📝</span>Clinical Notes</div>
                                                    {renderDescriptionContent(note.description || '')}
                                                </div>
                                            )}

                                            {(note.type === 'treatment' || note.type === 'appointment') && note.prescriptions && note.prescriptions.length > 0 && (
                                                <div className="note-section mt-2 mb-3">
                                                    <div className="section-title"><span className="icon">💊</span>Prescription</div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {note.prescriptions[0]?.treatments?.length > 0 && (
                                                            <div className="w-100 mb-2">
                                                                <div className="card border-0 bg-light">
                                                                    <div className="card-body p-3">
                                                                        <div className="row">
                                                                            {note.prescriptions[0].number && (
                                                                                <div className="col-md-6 mb-2">
                                                                                    <strong>Prescription #:</strong> {note.prescriptions[0].number}
                                                                                </div>
                                                                            )}
                                                                            {note.prescriptions[0].height && (
                                                                                <div className="col-md-6 mb-2">
                                                                                    <strong>Height:</strong> {note.prescriptions[0].height} cm
                                                                                </div>
                                                                            )}
                                                                            {note.prescriptions[0].weight && (
                                                                                <div className="col-md-6 mb-2">
                                                                                    <strong>Weight:</strong> {note.prescriptions[0].weight} kg
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {note.prescriptions[0].diagnosis && (
                                                                            <div className="col-md-6 mb-2">
                                                                                <strong>Diagnosis:</strong>
                                                                                <div style={{ whiteSpace: 'pre-line' }}>{note.prescriptions[0].diagnosis}</div>
                                                                            </div>
                                                                        )}
                                                                        <div className="mb-2">
                                                                            <strong>Treatment Plan:</strong>
                                                                            <ul className="mb-0 mt-2">
                                                                                {note.prescriptions[0].treatments.map((treatment, idx) => (
                                                                                    <li key={idx} style={{ whiteSpace: 'pre-line' }}>
                                                                                        {treatment.value}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                        {note.prescriptions[0] && (
                                                                            <div className="d-flex" style={{ gap: 0 }}>
                                                                                <div>
                                                                                    <CompactPrescriptionView
                                                                                        prescription={note.prescriptions[0]}
                                                                                        patientName={patientName || note.patient?.name}
                                                                                        className="rounded-start-0"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {Array.isArray(note.referrals) && note.referrals.length > 0 && (
                                                <div className="note-section mt-2">
                                                    <div className="section-title"><span className="icon">📤</span>Referrals</div>
                                                    <div className="d-flex flex-column gap-2">
                                                        {note.referrals.map((ref, rIdx) => {
                                                            const key = ref.id ?? `${note.id || 'note'}_${rIdx}`;
                                                            const isExpanded = !!expandedReferrals[key];
                                                            return (
                                                                <div key={ref.id || rIdx} className="referral-card p-2">
                                                                    <div className="d-flex align-items-start justify-content-between">
                                                                        <div className="me-2">
                                                                            {ref.to ? (
                                                                                <div className="referral-meta"><strong>To:</strong> {ref.to}</div>
                                                                            ) : (
                                                                                <div className="referral-meta"><strong>Referral</strong></div>
                                                                            )}
                                                                        </div>
                                                                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                                                            {typeof ref.content === 'string' && ref.content.trim() !== '' && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-secondary"
                                                                                    onClick={() => setExpandedReferrals(prev => ({ ...prev, [key]: !isExpanded }))}
                                                                                    aria-expanded={isExpanded}
                                                                                    title={isExpanded ? 'Hide details' : 'Show details'}
                                                                                    aria-label={isExpanded ? 'Hide details' : 'Show details'}
                                                                                >
                                                                                    <i className={`fas ${isExpanded ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                                                </button>
                                                                            )}
                                                                            {ref.id && (
                                                                                <div className="d-none d-sm-inline-flex">
                                                                                    <CompactReferralView referral={ref} />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    {isExpanded && ref.content && (
                                                                        <div
                                                                            className="mt-2"
                                                                            style={{ lineHeight: '1.5' }}
                                                                            dangerouslySetInnerHTML={{ __html: ref.content }}
                                                                        />
                                                                    )}
                                                                    {ref.id && (
                                                                        <div className="mt-2 d-flex justify-content-end d-sm-none">
                                                                            <CompactReferralView referral={ref} />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {note.media && note.media.length > 0 && (
                                                <div className="note-section mt-3">
                                                    <div className="row g-2">
                                                        {note.media.map((file, index) => {
                                                            const fileExt = file.original_filename?.split('.').pop().toLowerCase() || '';
                                                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt);
                                                            const isVideo = ['mp4', 'webm', 'mov', 'm4v'].includes(fileExt) || (String(file.mime_type || '').toLowerCase().startsWith('video/'));
                                                            const fileIcon = getFileIcon(file.original_filename);
                                                            const fileName = file.original_filename || `file_${index + 1}`;
                                                            return (
                                                                <div key={file.id || index} className="col-12 col-sm-6 col-lg-3">
                                                                    {isImage ? (
                                                                        <div
                                                                            className="card h-100 border-0 shadow-sm cursor-pointer"
                                                                            onClick={() => handleImageClick(file.url, fileName)}
                                                                            style={{ cursor: 'pointer' }}
                                                                        >
                                                                            <div className="ratio ratio-4x3 bg-light">
                                                                                <div className="d-flex align-items-center justify-content-center">
                                                                                    <img
                                                                                        src={file.url}
                                                                                        alt={fileName}
                                                                                        className="img-fluid"
                                                                                        style={{
                                                                                            maxHeight: '100%',
                                                                                            maxWidth: '100%',
                                                                                            objectFit: 'contain',
                                                                                            padding: '4px'
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="card-body p-2">
                                                                                <small className="d-block text-truncate" title={fileName}>
                                                                                    {fileName}
                                                                                </small>
                                                                            </div>
                                                                        </div>
                                                                    ) : isVideo ? (
                                                                        <div className="card h-100 border-0 shadow-sm">
                                                                            <div className="ratio ratio-4x3 bg-light">
                                                                                <video
                                                                                    src={file.url}
                                                                                    controls
                                                                                    preload="metadata"
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        objectFit: 'contain',
                                                                                        background: '#f8f9fa'
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                            <div className="card-body p-2">
                                                                                <small className="d-block text-truncate" title={fileName}>
                                                                                    {fileName}
                                                                                </small>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <a
                                                                            href={file.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-decoration-none text-dark"
                                                                        >
                                                                            <div className="card h-100 border-0 shadow-sm">
                                                                                <div className="ratio ratio-4x3 bg-light">
                                                                                    <div className="d-flex flex-column align-items-center justify-content-center">
                                                                                        <i className={`fas ${fileIcon} fa-2x text-muted mb-2`}></i>
                                                                                        <small className="text-muted">{fileExt.toUpperCase()}</small>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="card-body p-2">
                                                                                    <small className="d-block text-truncate" title={fileName}>
                                                                                        {fileName}
                                                                                    </small>
                                                                                </div>
                                                                            </div>
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <TimelineConnector show={index < timelineEntries.length - 1} />
                                </React.Fragment>
                            );
                        });
                    })()}
                </div>
            )}

            {/* Note Modal */}
            {showNoteModal && (
                <NoteModal 
                    show={showNoteModal} 
                    onHide={() => setShowNoteModal(false)}
                    onSubmit={onSubmit}
                    noteForm={noteForm}
                    onInputChange={onInputChange}
                    submitting={submitting}
                    submitError={submitError}
                    editingNote={editingNote}
                    patientData={patientData}
                />
            )}

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteConfirm} onHide={handleModalClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteError && (
                        <Alert variant="danger" className="mb-3">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {deleteError}
                        </Alert>
                    )}
                    <p>Are you sure you want to delete this note? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={handleModalClose}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={() => handleDeleteNote(deletingNoteId)}
                        disabled={!deletingNoteId || isDeleting}
                    >
                        {isDeleting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Deleting...
                            </>
                        ) : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Image Preview Modal */}
            <Modal show={showImageModal} onHide={handleCloseImageModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Image Preview: {currentImageName}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <img 
                        src={currentImage} 
                        alt={currentImageName} 
                        style={{ maxWidth: '100%', maxHeight: '70vh' }}
                        className="img-fluid"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseImageModal}>
                        Close
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => {
                            window.open(currentImage, '_blank');
                            handleCloseImageModal();
                        }}
                    >
                        Open in New Tab
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PatientNotes;
